"use client"

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react"
import { api } from "@/lib/api-client"

export interface AppUser {
  id: string
  display_name: string
  email: string | null
  avatar_url: string | null
  role: string
  status: string
}

interface AuthState {
  user: AppUser | null
  loading: boolean
  login: (idToken: string) => Promise<void>
  refreshUser: () => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

async function fetchUser(): Promise<AppUser | null> {
  const { accessToken } = api.getTokens()
  if (!accessToken) return null
  const res = await api.get("/api/v1/users/me")
  if (!res.ok) {
    api.clearTokens()
    return null
  }
  return res.json()
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchUser().then((u) => {
      setUser(u)
      setLoading(false)
    })
  }, [])

  const login = useCallback(async (idToken: string) => {
    const res = await api.post("/api/v1/auth/telegram/widget", { id_token: idToken })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: "Login failed" }))
      throw new Error(err.message || "Login failed")
    }
    const data = await res.json()
    api.setTokens(data.access_token, data.refresh_token)
    const u = await fetchUser()
    setUser(u)
  }, [])

  const refreshUser = useCallback(async () => {
    const u = await fetchUser()
    setUser(u)
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.post("/api/v1/auth/logout")
    } catch {
      /* ignore — tokens cleared anyway */
    }
    api.clearTokens()
    setUser(null)
    window.location.assign("/")
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, login, refreshUser, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within AuthProvider")
  return ctx
}
